// ============================================================================
// Assessment persistence.
// ============================================================================
// The widget computes a result locally for instant feedback, but the SERVER
// recomputes it from the raw answers and stores that as the authoritative
// record. Never treat the number the browser produced as the source of truth —
// an adviser acting on a tampered or stale client score is the failure mode
// this split exists to prevent.
// ============================================================================

import { getSessionId } from './funnelTracking';

const base_url = process.env.REACT_APP_API_BASE_URL || '';

/**
 * Persist a completed assessment.
 * @returns {Promise<object|null>} the server's authoritative result, or null if
 *          the call failed (the UI keeps showing the local result either way).
 */
export async function saveAssessment({ type, answers, result, language, apikey, conversationId: overrideConversationId, source }) {
  // The standalone employer page has no conversation of its own — it carries the
  // referring conversation id in the share link instead.
  const conversationId = overrideConversationId || localStorage.getItem('imavenconvoid');
  const sessionId = getSessionId();

  try {
    const response = await fetch(`${base_url}/assessments/save`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(conversationId ? { 'X-Conversation-ID': conversationId } : {}),
        ...(apikey ? { 'x-apikey': apikey } : {}),
      },
      body: JSON.stringify({
        type,
        answers,
        clientResult: result,
        sessionId: sessionId || null,
        expertId: localStorage.getItem('imavenexpertid') || null,
        language: language || 'en',
        source: source || 'widget',
      }),
    });

    if (!response.ok) return null;
    const data = await response.json();
    return data?.assessment || null;
  } catch (error) {
    console.warn('[Assessment] Failed to save:', error?.message);
    return null;
  }
}

// NOTE — there is deliberately no "write the score onto the funnel session" call
// here. Two reasons, both learned the hard way:
//
//   1. FunnelSession's schema (in @imaven/shared-models) has no assessment or
//      score fields, and mongoose strict mode drops unknown paths silently. The
//      pre-existing CRS call to PUT /funnel/session/:id/contact with `crsScore`
//      never persisted anything — and because storeContactInfo *requires*
//      userEmail or userPhone, it actually 400s and does nothing at all.
//   2. `leadLabel`, the one field that looks like a fit, is the adviser's own
//      configurable lead category (from expert.leadLabels). Writing an
//      assessment grade into it would clobber their categorisation.
//
// Assessments live in their own collection, keyed by conversationId and
// sessionId, and the dashboard joins them. See Maven-platform-Server
// /assessments/* for the read side.

/** Email the employer checklist and register the employer as a lead. */
export async function requestEmployerChecklist({ email, result, language, apikey, conversationId: overrideConversationId }) {
  const conversationId = overrideConversationId || localStorage.getItem('imavenconvoid');
  try {
    const response = await fetch(`${base_url}/assessments/employer-checklist`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(conversationId ? { 'X-Conversation-ID': conversationId } : {}),
        ...(apikey ? { 'x-apikey': apikey } : {}),
      },
      body: JSON.stringify({
        email,
        result,
        language: language || 'en',
        sessionId: getSessionId() || null,
        expertId: localStorage.getItem('imavenexpertid') || null,
      }),
    });
    return response.ok;
  } catch (error) {
    console.warn('[Assessment] Failed to request employer checklist:', error?.message);
    return false;
  }
}

/**
 * "Advise me on THIS application."
 *
 * Sends the Q&A transcript so the server can write the assessment into the
 * conversation as a real exchange and then run the same completion pipeline a
 * finished AI chat runs — session marked finished, conversation stamped
 * complete, case analysis triggered. The adviser gets one kind of case.
 */
export async function requestConsultation({ transcript, assessmentId, note, language, apikey }) {
  const conversationId = localStorage.getItem('imavenconvoid');
  if (!conversationId) return null;

  try {
    const response = await fetch(`${base_url}/assessments/request-consultation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Conversation-ID': conversationId,
        ...(apikey ? { 'x-apikey': apikey } : {}),
      },
      body: JSON.stringify({
        transcript,
        assessmentId: assessmentId || null,
        note: note || null,
        sessionId: getSessionId() || null,
        language: language || 'en',
      }),
    });
    if (!response.ok) return null;
    // Carries `adviserId`, which the caller needs to build the platform hand-off
    // link. It is returned here rather than fetched separately because the
    // endpoint the widget used to ask (GET /chat/getAdvisorData) does not exist.
    return await response.json();
  } catch (error) {
    console.warn('[Assessment] Failed to request consultation:', error?.message);
    return null;
  }
}

/**
 * Subscribe to draw / cutoff alerts. The last-known score rides along so the
 * alert email can compare it against a published cutoff — and stays null when no
 * score could be published, so the email omits the comparison rather than
 * implying a zero.
 */
export async function subscribeToDrawAlerts({ email, result, language, apikey }) {
  const conversationId = localStorage.getItem('imavenconvoid');
  try {
    const response = await fetch(`${base_url}/assessments/alerts/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(conversationId ? { 'X-Conversation-ID': conversationId } : {}),
        ...(apikey ? { 'x-apikey': apikey } : {}),
      },
      body: JSON.stringify({
        email,
        language: language || 'en',
        sessionId: getSessionId() || null,
        expertId: localStorage.getItem('imavenexpertid') || null,
        score: result?.total ?? null,
        maxScore: result?.maxScore ?? null,
        verdict: result?.verdict || null,
        source: 'oinp-results',
      }),
    });
    return response.ok;
  } catch (error) {
    console.warn('[Assessment] Failed to subscribe to alerts:', error?.message);
    return false;
  }
}

/** Register a no-job-offer lead for the "notify me" capture. */
export function registerNotifyMe({ email, language, apikey }) {
  fetch(`${base_url}/assessments/notify-me`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(apikey ? { 'x-apikey': apikey } : {}),
    },
    body: JSON.stringify({
      email,
      language: language || 'en',
      topic: 'oinp-job-offer-strategy',
      sessionId: getSessionId() || null,
      expertId: localStorage.getItem('imavenexpertid') || null,
    }),
  }).catch(() => {});
}

/** Shareable standalone employer-check link the candidate can forward. */
export function employerCheckShareUrl(apikey) {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const conversationId = localStorage.getItem('imavenconvoid') || '';
  const ref = conversationId ? `?ref=${encodeURIComponent(conversationId)}` : '';
  return `${origin}/employer-check/${apikey}${ref}`;
}

/**
 * Branding for an assessment-first landing page. No conversation exists yet, so
 * this is the only way the page knows whose practice it belongs to.
 */
export async function anonymousEntryContext(apikey) {
  try {
    const response = await fetch(`${base_url}/assessments/anonymous/expert`, {
      headers: { ...(apikey ? { 'x-apikey': apikey } : {}) },
    });
    if (!response.ok) return null;
    return await response.json();
  } catch (error) {
    console.warn('[Assessment] Could not load entry context:', error?.message);
    return null;
  }
}

/**
 * Convert an anonymous assessment into a real case.
 *
 * This is the ONLY moment contact details are asked for on the assessment-first
 * path, and the only moment a conversation, a lead and a dashboard entry come
 * into existence. Returns { adviserId, conversationId } so the page can build
 * the same platform hand-off link a finished chat produces.
 */
export async function anonymousConsultation({
  type, answers, result, transcript, note, contact, language, apikey, source, assessmentId,
}) {
  try {
    const response = await fetch(`${base_url}/assessments/anonymous/consultation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(apikey ? { 'x-apikey': apikey } : {}),
      },
      body: JSON.stringify({
        type,
        answers,
        clientResult: result,
        transcript,
        note,
        contact,
        // Attach to the record already stored while anonymous, so one person
        // does not end up with two assessments.
        assessmentId: assessmentId || null,
        sessionId: getSessionId() || null,
        language: language || 'en',
        source: source || 'assessment-link',
      }),
    });
    if (!response.ok) return null;
    return await response.json();
  } catch (error) {
    console.warn('[Assessment] Anonymous consultation failed:', error?.message);
    return null;
  }
}
